import {SelectOption} from './types';

export type URLParamsSerializer<T> = (params: URLSearchParams, payload: T) => void;

export type WithPaginationURLParams<T extends object = {}> = T & {limit?: number; offset?: number};
export const withPaginationURLParams =
  <T extends object = {}>(
    next: URLParamsSerializer<WithPaginationURLParams<T>> = () => {},
  ): URLParamsSerializer<WithPaginationURLParams<T>> =>
  (params, payload) => {
    if (payload.limit !== undefined) params.set('limit', `${payload.limit}`);
    if (payload.offset !== undefined) params.set('offset', `${payload.offset}`);
    next(params, payload);
  };

export type WithSearchURLParams<T extends object = {}> = T & {search?: string};
export const withSearchURLParams =
  <T extends object = {}>(
    next: URLParamsSerializer<WithSearchURLParams<T>> = () => {},
  ): URLParamsSerializer<WithSearchURLParams<T>> =>
  (params, payload) => {
    if (payload.search) params.set('search', payload.search);
    next(params, payload);
  };

export type WithOrderingURLParams<T extends object = {}> = T & {ordering?: string};
export const withOrderingURLParams =
  <T extends object = {}>(
    next: URLParamsSerializer<WithOrderingURLParams<T>> = () => {},
  ): URLParamsSerializer<WithOrderingURLParams<T>> =>
  (params, payload) => {
    if (payload.ordering) params.set('ordering', payload.ordering);
    next(params, payload);
  };

export type WithCampaignIdURLParams<T extends object = {}> = T & {campaign_id?: number};
export const withCampaignIdURLParams =
  <T extends object = {}>(
    next: URLParamsSerializer<WithCampaignIdURLParams<T>> = () => {},
  ): URLParamsSerializer<WithCampaignIdURLParams<T>> =>
  (params, payload) => {
    if (payload.campaign_id !== undefined) params.set('campaign_id', `${payload.campaign_id}`);
    next(params, payload);
  };

export type WithFilterURLParams<T extends object = {}, O = string> = T & {
  filter?: Record<
    string,
    string | number | boolean | SelectOption<O> | Array<string | number | boolean | SelectOption<O>>
  >;
};
export const withFilterURLParams =
  <T extends object = {}, O = string>(
    next: URLParamsSerializer<WithFilterURLParams<T, O>> = () => {},
  ): URLParamsSerializer<WithFilterURLParams<T, O>> =>
  (params, payload) => {
    if (payload.filter) {
      Object.entries(payload.filter).forEach(([key, value]) => {
        let paramKey = key.replace('.', '__');
        let paramValue = value;
        if (Array.isArray(value)) {
          paramKey = `${paramKey}__in`;
          paramValue = value.map(e => String(typeof e === 'object' && 'value' in e ? e.value : e));
        } else if (typeof value === 'object') {
          paramValue = String(value.value);
        } else {
          paramValue = String(value);
        }
        params.set(paramKey, paramValue);
      });
    }
    next(params, payload);
  };
